import PropTypes from "prop-types";
import VuiBox from "components/ui/VuiBox";
import VuiTypography from "components/ui/VuiTypography";
import { GLASS_CARD, GRADIENTS, CHART_COLORS, ACCENT_COLORS } from "utils/designTokens";

// Stable colour per source name — hashes the string to a chart colour.
const sourceColor = (name) => {
  if (!name) return CHART_COLORS[0];
  let h = 0;
  for (let i = 0; i < name.length; i++) {
    h = ((h << 5) - h) + name.charCodeAt(i);
    h |= 0;
  }
  return CHART_COLORS[Math.abs(h) % CHART_COLORS.length];
};

// Format ISO date string — relative if recent, absolute if older
const formatDate = (dateStr) => {
  if (!dateStr) return "";
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    const now = new Date();
    const diffMs = now - d;
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffDays === 0) return "Today";
    if (diffDays === 1) return "Yesterday";
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 14) return "Last week";
    if (diffDays < 21) return "2 weeks ago";
    if (diffDays < 28) return "3 weeks ago";
    const diffMonths = Math.round(diffDays / 30);
    if (diffMonths <= 1) return "1 month ago";
    if (diffMonths <= 6) return `${diffMonths} months ago`;
    return d.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
  } catch {
    return dateStr;
  }
};

// Strip URLs (http/https and short links like lnkd.in) from text
const stripUrls = (text) => {
  if (!text) return text;
  return text
    .replace(/https?:\/\/\S+/g, "")   // full URLs
    .replace(/\S+\.in\/\S+/g, "")     // short links like lnkd.in/xxxxx
    .replace(/\s{2,}/g, " ")          // collapse multiple spaces
    .trim();
};

// Truncate to a comfortable reading length without cutting mid-word
const truncate = (text, maxChars = 160) => {
  if (!text || text.length <= maxChars) return text;
  const cut = text.lastIndexOf(" ", maxChars);
  return text.slice(0, cut > 0 ? cut : maxChars) + "…";
};

/**
 * PressArticleCard
 *
 * A focused card for a single press article. Clicking anywhere on the card
 * opens the article — no separate "Read article" link needed.
 *
 * Design decisions:
 * - Whole card is clickable (not just the title link) — reduces UI noise
 * - Summary truncated to ~140 chars — the card communicates the gist; full
 *   article is one click away
 * - No redundant "Read article ↗" link — the card interaction is self-evident
 * - Topic tag is clickable to filter the feed (calls onTopicClick)
 * - Product mention tags (up to 3) surface competitive signals inline
 *
 * Props:
 *   title          — article title (string)
 *   url            — link to full article (string)
 *   summary        — LLM-extracted summary, truncated for display (string)
 *   topic          — LLM-extracted topic phrase, e.g. "Flow Diverter Outcomes" (string)
 *   source         — press source name, e.g. "NeuroNews" (string)
 *   date           — ISO date string (string)
 *   productMentions — array of { canonicalName, mentionedName } (optional)
 *   onTopicClick   — (topic: string) => void — called when the topic tag is clicked
 */
function PressArticleCard({ title, url, summary, topic, source, date, productMentions, onTopicClick }) {

  const color = sourceColor(source);
  // Summary is the primary content — strip URLs and show in full (no truncation needed
  // once URLs are gone; the summary itself is already concise from the LLM)
  const displaySummary = stripUrls(summary);

  const handleCardClick = (e) => {
    // Don't navigate if the user clicked the topic tag
    if (e.defaultPrevented) return;
    if (url) window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <VuiBox
      onClick={handleCardClick}
      sx={{
        background: GLASS_CARD.background,
        backdropFilter: GLASS_CARD.backdropFilter,
        borderRadius: "16px",
        border: "1px solid rgba(255,255,255,0.07)",
        p: "16px 18px",
        position: "relative",
        overflow: "hidden",
        cursor: url ? "pointer" : "default",
        transition: "border-color 0.18s ease, background 0.18s ease",
        "&:hover": {
          borderColor: "rgba(255,255,255,0.16)",
          background: "rgba(255,255,255,0.10)",
        },
        // Left accent bar in source colour
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0, left: 0, bottom: 0,
          width: "3px",
          borderRadius: "16px 0 0 16px",
          background: color,
          opacity: 0.7,
        },
      }}
    >
      {/* Row 1: source badge left — content tags right */}
      <VuiBox display="flex" alignItems="center" gap={0.625} mb={0.875}>
        {/* Source badge — identity anchor, far left */}
        <VuiBox
          sx={{
            px: 0.875,
            py: 0.2,
            borderRadius: "5px",
            background: `${color}22`,
            border: `1px solid ${color}44`,
            flexShrink: 0,
          }}
        >
          <VuiTypography
            variant="caption"
            sx={{ fontSize: "0.68rem", color, fontWeight: "700", letterSpacing: "0.04em" }}
          >
            {source || "Press"}
          </VuiTypography>
        </VuiBox>

        {/* Spacer */}
        <VuiBox sx={{ flex: 1 }} />

        {/* Content tags — far right */}
        <VuiBox display="flex" alignItems="center" gap={0.625} flexShrink={0}>
          {/* Topic tag */}
          {topic && (
            <VuiBox
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (onTopicClick) onTopicClick(topic);
              }}
              sx={{
                px: 0.875,
                py: 0.2,
                borderRadius: "5px",
                background: "rgba(37,99,235,0.15)",
                border: "1px solid rgba(37,99,235,0.28)",
                cursor: onTopicClick ? "pointer" : "default",
                transition: "background 0.15s ease",
                "&:hover": onTopicClick ? { background: "rgba(37,99,235,0.25)" } : {},
              }}
            >
              <VuiTypography
                variant="caption"
                sx={{ fontSize: "0.68rem", color: "#93c5fd", fontWeight: "600" }}
              >
                {topic}
              </VuiTypography>
            </VuiBox>
          )}

          {/* Product mention tags */}
          {productMentions && productMentions.slice(0, 2).map((pm) => (
            <VuiBox
              key={pm.canonicalName || pm.mentionedName}
              sx={{
                px: 0.875,
                py: 0.2,
                borderRadius: "5px",
                background: "rgba(167,139,250,0.10)",
                border: "1px solid rgba(167,139,250,0.22)",
                flexShrink: 0,
              }}
            >
              <VuiTypography
                variant="caption"
                sx={{ fontSize: "0.68rem", color: "#c4b5fd", fontWeight: "500" }}
              >
                {pm.canonicalName || pm.mentionedName}
              </VuiTypography>
            </VuiBox>
          ))}
        </VuiBox>
      </VuiBox>

      {/* Summary */}
      {displaySummary && (
        <VuiTypography
          variant="caption"
          sx={{
            fontSize: "0.81rem",
            color: "rgba(255,255,255,0.75)",
            lineHeight: 1.6,
            display: "block",
            mb: 0.75,
          }}
        >
          {displaySummary}
        </VuiTypography>
      )}

      {/* Date — bottom of card, consistent with PostFeedCard */}
      {date && (
        <VuiTypography
          variant="caption"
          sx={{ fontSize: "0.7rem", color: "rgba(255,255,255,0.28)", display: "block" }}
        >
          {formatDate(date)}
        </VuiTypography>
      )}
    </VuiBox>
  );
}

PressArticleCard.propTypes = {
  title: PropTypes.string.isRequired,
  url: PropTypes.string,
  summary: PropTypes.string,
  topic: PropTypes.string,
  source: PropTypes.string,
  date: PropTypes.string,
  productMentions: PropTypes.arrayOf(
    PropTypes.shape({
      canonicalName: PropTypes.string,
      mentionedName: PropTypes.string,
    })
  ),
  onTopicClick: PropTypes.func,
};

PressArticleCard.defaultProps = {
  url: null,
  summary: null,
  topic: null,
  source: null,
  date: null,
  productMentions: [],
  onTopicClick: null,
};

export default PressArticleCard;
